import { css } from '@emotion/css';
import { Users, Tag, Gift, Zap } from 'lucide-react';
import { ContactForm } from '../../../shared/components/contact-form';

const page = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'var(--primary-color)',
  minHeight: 'calc(100vh - 100px)',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-md)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-sm)',
  },
});

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xl)',
});

const header = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const title = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-700)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-600)',
  },
});

const subtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
  lineHeight: 1.6,
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-450)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const section = css({
  marginBottom: 'var(--spacing-lg)',
});

const sectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-md)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const benefitsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 'var(--spacing-lg)',
  marginTop: 'var(--spacing-lg)',
});

const benefitCard = css({
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  borderRadius: 'var(--spacing-md)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

const benefitIcon = css({
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-xs)',
});

const benefitTitle = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-2xs)',
});

const benefitText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
});

const content = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-800)',
  lineHeight: 1.8,
  marginBottom: 'var(--spacing-md)',
  '& ul': {
    listStyle: 'disc',
    paddingLeft: 'var(--spacing-xl)',
    marginTop: 'var(--spacing-sm)',
    '& li': {
      marginBottom: 'var(--spacing-xs)',
    },
  },
});

const highlight = css({
  padding: 'var(--spacing-md)',
  backgroundColor: 'rgba(var(--pcrgba-50), 0.5)',
  borderLeft: '4px solid var(--pc-600)',
  borderRadius: '0 var(--spacing-sm) var(--spacing-sm) 0',
  marginTop: 'var(--spacing-md)',
  marginBottom: 'var(--spacing-md)',
  '& p': {
    margin: 0,
    color: 'var(--g-800)',
    fontSize: 'var(--fs-400)',
    lineHeight: 1.6,
  },
});

export function GroupSales() {
  return (
    <div className={page}>
      <div className={container}>
        <div className={header}>
          <h1 className={title}>Vendas para Grupos</h1>
          <p className={subtitle}>
            Condições especiais para empresas, escolas, associações e grupos que querem levar mais
            pessoas aos eventos.
          </p>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Vantagens de Comprar em Grupo</h2>
          <div className={content}>
            <p>
              Nossa solução de vendas para grupos oferece condições diferenciadas para empresas e
              organizações que desejam proporcionar experiências únicas a seus colaboradores,
              alunos ou membros.
            </p>
          </div>

          <div className={benefitsGrid}>
            <div className={benefitCard}>
              <Tag className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Descontos Progressivos</h3>
              <p className={benefitText}>
                Quanto maior o grupo, maior o desconto. Oferecemos condições especiais a partir de
                10 ingressos.
              </p>
            </div>

            <div className={benefitCard}>
              <Users className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Gestão Simplificada</h3>
              <p className={benefitText}>
                Compre todos os ingressos de uma vez e distribua facilmente entre os participantes
                do grupo.
              </p>
            </div>

            <div className={benefitCard}>
              <Gift className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Benefícios Exclusivos</h3>
              <p className={benefitText}>
                Acesso a áreas VIP, cortesias para organizadores e outras vantagens exclusivas para
                grupos.
              </p>
            </div>

            <div className={benefitCard}>
              <Zap className={benefitIcon} size={40} />
              <h3 className={benefitTitle}>Atendimento Prioritário</h3>
              <p className={benefitText}>
                Equipe dedicada para atender suas necessidades e garantir uma experiência perfeita
                do início ao fim.
              </p>
            </div>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Para Quem é Ideal</h2>
          <div className={content}>
            <ul>
              <li>
                <strong>Empresas:</strong> Eventos corporativos, confraternizações, team building e
                ações de endomarketing.
              </li>
              <li>
                <strong>Escolas e Universidades:</strong> Excursões culturais, formaturas e eventos
                acadêmicos.
              </li>
              <li>
                <strong>Clubes e Associações:</strong> Passeios, encontros e atividades para
                membros.
              </li>
              <li>
                <strong>Grupos de Amigos:</strong> Aniversários, celebrações e reuniões especiais.
              </li>
              <li>
                <strong>Agências de Turismo:</strong> Pacotes e roteiros com eventos incluídos.
              </li>
            </ul>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Como Funciona</h2>
          <div className={content}>
            <p>O processo de compra para grupos é simples e personalizado:</p>
            <ul>
              <li>Entre em contato através do formulário abaixo informando o evento de interesse</li>
              <li>Nossa equipe preparará uma proposta personalizada com condições especiais</li>
              <li>Após aprovação, você receberá um link exclusivo para finalizar a compra</li>
              <li>Os ingressos podem ser enviados individualmente ou em lote</li>
              <li>Suporte dedicado durante todo o processo até o dia do evento</li>
            </ul>

            <div className={highlight}>
              <p>
                <strong>Importante:</strong> As condições especiais para grupos estão sujeitas à
                disponibilidade de ingressos e política de cada evento. Entre em contato com
                antecedência para garantir as melhores condições.
              </p>
            </div>
          </div>
        </div>

        <div className={section}>
          <h2 className={sectionTitle}>Solicite uma Proposta</h2>
          <div className={content}>
            <p>
              Interessado em levar seu grupo a um evento incrível? Preencha o formulário abaixo com
              as informações do seu grupo e do evento desejado. Nossa equipe entrará em contato em
              até 24 horas com uma proposta personalizada.
            </p>
          </div>
          <ContactForm
            defaultSubject="Vendas para Grupos"
            defaultMessage="Olá! Gostaria de solicitar uma proposta para compra de ingressos em grupo. 

Informações do Grupo:
- Número de pessoas: 
- Tipo de grupo (empresa, escola, etc.): 
- Evento de interesse: 
- Data desejada: 

Aguardo retorno. Obrigado!"
          />
        </div>
      </div>
    </div>
  );
}

export default GroupSales;
